import React, { useState, useRef, useEffect } from "react";
import EverTrue from "app";
import PropTypes from "prop-types";
import { Button, Icon, Loading } from "@evertrue/et-components";
import ToastAlert from "apps/layout/components/toast-alert";
import ThankviewModal from "./thankview-modal";
import ThankviewRecorder from "./thankview-recorder";
import ThankviewLibrary from "./thankview-library";
import ThankviewDefaultSettings from "./thankview-default-settings";
import { ModalProvider, useModal } from "./hooks/modal-context";
import { RecorderProvider, useRecorder } from "./hooks/recorder-context";
import UploadVideo from "./upload-video";

const MODAL_NAMES = {
  RECORDER: "Record Your Video",
  LIBRARY: "Video Library",
  LIBRARY_STATE: "Video Library",
  DEFAULT_SETTINGS: "Set Video Defaults",
  UPLOAD_VIDEO: "Upload Video",
};

const DropdownMenu = ({ isOpen, dropdownRef, menuItems }) => {
  if (!isOpen) return null;

  return (
    <div className="tv-dropdown-content" ref={dropdownRef}>
      {menuItems.map((item) => (
        <a key={item.label} onClick={item.onClick} className="tv-dropdown-item">
          {item.icon && <Icon icon={item.icon} iconKit={item.iconKit} className="tv-dropdown-item-icon" />}
          <span className="tv-dropdown-item-label">{item.label}</span>
        </a>
      ))}
    </div>
  );
};

const Modal = ({
  width,
  height,
  name,
  isOpen,
  closeModal,
  children,
  title,
  onSubmit,
  submitLabel,
  disableSubmit,
  size,
  closeLabel,
  withActions,
}) => (
  <ThankviewModal
    isOpen={isOpen}
    closeModal={closeModal}
    title={title}
    onSubmit={onSubmit}
    submitLabel={submitLabel}
    closeLabel={closeLabel}
    width={width}
    height={height}
    disableSubmit={disableSubmit}
    withActions={withActions}
    size={size}
  >
    {children}
  </ThankviewModal>
);

const ThankviewCTA = () => {
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { modalState, openModal, closeModal } = useModal();
  const { updateState, state, handleToastClose, selectedVideo, handleLibraryVideoReview } = useRecorder();
  const { isUploading, isToastOpen } = state;
  const toggleDropdown = () => setDropdownIsOpen(!dropdownIsOpen);
  const uploadVideoRef = useRef(null);
  useEffect(() => {
    if (dropdownIsOpen && dropdownRef.current) {
      const dropdownHeight = dropdownRef.current.offsetHeight;
      dropdownRef.current.style.top = `-${dropdownHeight + 5}px`;
    }
  }, [dropdownIsOpen]);

  const handleReviewLibraryVideo = async () => {
    try {
      updateState({
        isReviewing: true,
      });
      await handleLibraryVideoReview(selectedVideo);
      openModal(MODAL_NAMES.RECORDER, ThankviewRecorder, {
        size: "1060px",
        height: "735px",
        withActions: false,
      });
    } catch (error) {
      EverTrue.Alert.error("Error reviewing library video");
      updateState({
        isReviewing: true,
      });
    } finally {
      updateState({
        isReviewing: true,
      });
    }
  };

  const handleUploadClick = async () => {
    try {
      updateState({ isProcessing: true });
      const file = await uploadVideoRef.current.triggerFileInput();
      toggleDropdown();
      if (file) {
        setTimeout(() => {
          openModal(MODAL_NAMES.RECORDER, ThankviewRecorder, {
            size: "1060px",
            height: "735px",
            withActions: false,
          });
        }, 300);
      } else {
        updateState({ isProcessing: false });
      }
    } catch (error) {
      EverTrue.Alert.error("Error uploading video");
      updateState({ isProcessing: false });
    }
  };
  const menuItems = [
    {
      id: "record",
      label: "Record New Video",
      onClick: () => {
        toggleDropdown();
        openModal(MODAL_NAMES.DEFAULT_SETTINGS, ThankviewDefaultSettings, {
          size: "medium",
          withActions: true,
          submitLabel: "Confirm",
          onSubmit: () => {
            openModal(MODAL_NAMES.RECORDER, ThankviewRecorder, {
              size: "1060px",
              height: "800px",
              withActions: false,
            });
          },
        });
      },
      icon: "tv-video",
      iconKit: "tv",
    },
    {
      id: "library",
      label: "Video Library",
      onClick: () => {
        toggleDropdown();
        openModal(MODAL_NAMES.LIBRARY_STATE, ThankviewLibrary, {
          size: "medium",
          withActions: true,
          submitLabel: "Confirm",
          closeLabel: "Close",
          disableSubmit: state.isSelectedLibraryVideosMoreThanOne ? true : false,
          onSubmit: () => {
            toggleDropdown();
            if (!state.isSelectedLibraryVideosMoreThanOne) {
              handleReviewLibraryVideo();
            }
          },
        });
      },
      icon: "logo",
      iconKit: "tv",
    },
    {
      id: "upload-video",
      label: "Upload Video",
      onClick: handleUploadClick,
      icon: "tv-upload",
      iconKit: "tv",
    },
  ];
  const handleFileSelect = (file) => {
    closeModal();
  };
  return (
    <>
      <div className="tv-video-upload-button">
        <Button className="et--button-secondary tv-cta-button--margin" onClick={toggleDropdown}>
          <Icon icon="tv-video" iconKit="tv" />
        </Button>
        <DropdownMenu isOpen={dropdownIsOpen} dropdownRef={dropdownRef} menuItems={menuItems} />
        {isUploading && <Loading />}
        {modalState.openModal && (
          <Modal
            name={modalState.openModal}
            isOpen={true}
            closeModal={closeModal}
            title={modalState.openModal}
            {...modalState.modalProps}
          >
            {modalState.currentComponent && React.createElement(modalState.currentComponent)}
          </Modal>
        )}
        <UploadVideo ref={uploadVideoRef} onFileSelect={handleFileSelect} />
        {isToastOpen && (
          <div className="toast-container">
            <ToastAlert type={"success"} icon={"gt-icon-access-time"} onClose={handleToastClose}>
              <h5 className="toast--header">Successfully Copied!</h5>
            </ToastAlert>
          </div>
        )}
      </div>
    </>
  );
};

Modal.propTypes = {
  name: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  onSubmit: PropTypes.func,
  width: PropTypes.string,
  height: PropTypes.string,
  title: PropTypes.string.isRequired,
  submitLabel: PropTypes.string,
  closeLabel: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.oneOf(["small", "medium", "large", "default", "fullscreen"]), PropTypes.string]),
  withActions: PropTypes.bool,
  disableSubmit: PropTypes.bool,
};

DropdownMenu.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  dropdownRef: PropTypes.object,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      icon: PropTypes.string,
      iconKit: PropTypes.oneOf(["et", "tv", "gt"]),
    })
  ).isRequired,
};

const WrappedThankviewCTA = () => (
  <RecorderProvider>
    <ModalProvider>
      <ThankviewCTA />
    </ModalProvider>
  </RecorderProvider>
);

export default WrappedThankviewCTA;
